import React, { useState, useCallback } from 'react';
import { Problem, Outcome } from '../types/problem';
import { evaluateAnswer, ApiError } from '../hooks/useApi';
import { Link } from '../router';
import { formatDate, plural } from '../utils/format';
import Icon, { IconName } from './Icon';
import Timer from './Timer';

interface RevisionCardProps {
  problem: Problem;
  index: number;
  total: number;
  maxInterval: number;
  onNext: (outcome: Outcome) => Promise<void>;
}

type Verdict = 'correct' | 'wrong' | 'timeout' | 'revealed' | 'error' | 'quota';

const VERDICTS: Record<Verdict, { tone: string; icon: IconName; title: string; text?: string }> = {
  correct: { tone: 'success', icon: 'check', title: 'Recalled' },
  wrong: { tone: 'danger', icon: 'x', title: 'Not quite' },
  timeout: { tone: 'warning', icon: 'clock', title: "Time's up", text: 'Compare with your saved approach below.' },
  revealed: { tone: 'warning', icon: 'eye', title: 'Answer revealed', text: 'Compare with your saved approach below.' },
  error: {
    tone: 'warning',
    icon: 'alert',
    title: "Couldn't grade automatically",
    text: 'Compare your answer with the saved approach and grade yourself.',
  },
  quota: {
    tone: 'warning',
    icon: 'info',
    title: 'AI grades used up for this month',
    text: 'Compare with your saved approach and grade yourself, or upgrade your plan for more AI grading.',
  },
};

export default function RevisionCard({ problem, index, total, maxInterval, onNext }: RevisionCardProps) {
  const [answer, setAnswer] = useState('');
  const [evaluating, setEvaluating] = useState(false);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState<Outcome | null>(null);
  const [saveError, setSaveError] = useState('');

  const handleTimeout = useCallback(() => {
    setVerdict(v => v ?? 'timeout');
  }, []);

  async function handleSubmit() {
    if (!answer.trim() || evaluating) return;
    setEvaluating(true);
    try {
      const res = await evaluateAnswer({
        problemTitle: problem.title,
        approach: problem.idea || '',
        notes: problem.notes || '',
        userAnswer: answer,
      });
      setFeedback(res.feedback);
      setVerdict(res.correct ? 'correct' : 'wrong');
    } catch (err) {
      setVerdict(err instanceof ApiError && err.code === 'quota_exceeded' ? 'quota' : 'error');
    } finally {
      setEvaluating(false);
    }
  }

  async function finish(outcome: Outcome) {
    setSaving(outcome);
    setSaveError('');
    try {
      await onNext(outcome);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save your review');
      setSaving(null);
    }
  }

  const nextInterval = Math.min(Math.max(problem.interval, 1) * 2, maxInterval);
  const lastReviewed = problem.revisions.length
    ? problem.revisions[problem.revisions.length - 1]
    : null;
  const v = verdict ? VERDICTS[verdict] : null;

  return (
    <div className="page-narrow">
      <div className="progress" aria-hidden="true">
        <div className="progress-fill" style={{ width: `${((index + (verdict ? 1 : 0)) / total) * 100}%` }} />
      </div>

      <section className="card">
        <div className="card-body">
          <div className="problem-head">
            <div>
              <div className="eyebrow">Problem {index + 1} of {total}</div>
              <a href={problem.url} target="_blank" rel="noopener noreferrer" className="problem-title">
                {problem.title}
                <Icon name="external" size={16} />
              </a>
            </div>
            <Timer duration={300} onTimeout={handleTimeout} paused={verdict !== null || evaluating} />
          </div>
          <div className="problem-meta">
            <span className="badge">Solved {plural(problem.solveCount, 'time')}</span>
            <span className="badge">
              {lastReviewed ? `Last reviewed ${formatDate(lastReviewed)}` : 'First review'}
            </span>
          </div>
        </div>

        {!verdict && (
          <>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <label className="field-label" htmlFor="answer">What's the core idea and optimal approach?</label>
              <p className="field-help">Name the data structure, the algorithm, and the key insight.</p>
              <textarea
                id="answer"
                className="textarea"
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
                }}
                placeholder="e.g. Sliding window with a hash map of last-seen indices; move the left edge past duplicates…"
                disabled={evaluating}
                autoFocus
              />
            </div>
            <div className="card-footer">
              <span className="hint"><kbd>Ctrl</kbd>+<kbd>Enter</kbd> to submit</span>
              <span className="spacer" />
              <button className="btn btn-ghost" onClick={() => setVerdict('revealed')} disabled={evaluating}>
                <Icon name="eye" size={14} /> Show answer
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={!answer.trim() || evaluating}>
                {evaluating ? <><span className="spinner" /> Checking…</> : 'Submit answer'}
              </button>
            </div>
          </>
        )}

        {v && (
          <>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <div className={`verdict verdict-${v.tone}`} role="status">
                <span className="verdict-icon"><Icon name={v.icon} size={15} /></span>
                <div>
                  <div className="verdict-title">{v.title}</div>
                  <div className="verdict-text">
                    {feedback || v.text}
                    {verdict === 'quota' && <> <Link to="/pricing" className="btn-link">See plans</Link></>}
                  </div>
                </div>
              </div>

              <div className={`compare ${answer.trim() ? '' : 'compare-single'}`}>
                {answer.trim() && (
                  <div className="panel">
                    <div className="panel-label"><span className="dot accent" />Your answer</div>
                    <div className="panel-body">{answer}</div>
                  </div>
                )}
                <div className="panel">
                  <div className="panel-label"><span className="dot success" />Saved approach</div>
                  <div className={`panel-body ${problem.idea ? '' : 'muted'}`}>
                    {problem.idea || 'No approach recorded for this problem.'}
                  </div>
                </div>
              </div>

              {problem.notes && (
                <div className="panel notes-panel">
                  <div className="panel-label"><span className="dot" />Notes</div>
                  <div className="panel-body">{problem.notes}</div>
                </div>
              )}
            </div>

            <div className="card-footer">
              {saveError ? (
                <span className="error-text">{saveError}. Check the server and try again.</span>
              ) : (
                <span className="next-hint">
                  Recalled: back in {plural(nextInterval, 'day')} · Missed: back tomorrow
                </span>
              )}
              <span className="spacer" />
              <button className="btn btn-ghost" onClick={() => finish('skipped')} disabled={saving !== null}>
                Skip
              </button>
              {verdict === 'correct' ? (
                <button className="btn btn-primary" onClick={() => finish('recalled')} disabled={saving !== null}>
                  {saving === 'recalled' && <span className="spinner" />}
                  Mark recalled <Icon name="arrowRight" size={14} />
                </button>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={() => finish('recalled')} disabled={saving !== null}>
                    {saving === 'recalled' && <span className="spinner" />}
                    {verdict === 'error' || verdict === 'quota' ? 'I recalled it' : 'I knew it'}
                  </button>
                  <button className="btn btn-primary" onClick={() => finish('missed')} disabled={saving !== null}>
                    {saving === 'missed' && <span className="spinner" />}
                    Mark missed <Icon name="arrowRight" size={14} />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
